import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, TIER_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RankBadge } from "@/components/RankBadge";
import { StreakBadge } from "@/components/StreakBadge";
import { RoleBadge } from "@/components/RoleBadge";
import { BannerDisplay, RARITY_META } from "@/components/Banner";
import { Crown, Skull, Leaf, Calendar, Trophy, MessageSquare, ArrowLeft, ShoppingBag, Sparkles, Check, X, Archive } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch (e) { return "—"; }
};

export default function Profile() {
    const { userId } = useParams();
    const { user: me, refresh } = useAuth();
    const nav = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Banner vault state (only when viewing own profile)
    const [vault, setVault] = useState([]);
    const [vaultLoading, setVaultLoading] = useState(false);
    const [equippedId, setEquippedId] = useState(null);
    const [busyBannerId, setBusyBannerId] = useState(null);

    const isOwnProfile = me?.id && profile?.id && me.id === profile.id;

    const loadVault = useCallback(async () => {
        setVaultLoading(true);
        try {
            const res = await api.get("/users/me/banners");
            setVault(res.data.items || []);
            setEquippedId(res.data.equipped_id || null);
        } catch (e) { /* noop */ }
        finally { setVaultLoading(false); }
    }, []);

    useEffect(() => {
        if (isOwnProfile) loadVault();
    }, [isOwnProfile, loadVault]);

    const reloadProfile = useCallback(async () => {
        if (!userId) return;
        try {
            const res = await api.get(`/users/${userId}`);
            setProfile(res.data);
        } catch (e) { /* noop */ }
    }, [userId]);

    const equipBanner = async (banner) => {
        const isEquipped = equippedId === banner.id;
        setBusyBannerId(banner.id);
        try {
            const res = await api.post("/shop/banners/equip", { banner_id: isEquipped ? null : banner.id });
            setEquippedId(res.data.equipped_banner_id);
            toast.success(isEquipped ? "Banner unequipped" : `${banner.name} equipped \u2728`);
            await Promise.all([refresh(), reloadProfile()]);
        } catch (e) {
            toast.error("Failed to update banner");
        } finally {
            setBusyBannerId(null);
        }
    };

    useEffect(() => {
        let alive = true;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await api.get(`/users/${userId}`);
                if (alive) setProfile(res.data);
            } catch (e) {
                if (alive) setError(e?.response?.status === 404 ? "Profile not found" : "Failed to load profile");
            } finally { if (alive) setLoading(false); }
        };
        if (userId) load();
        return () => { alive = false; };
    }, [userId]);

    const tier = profile?.effective_tier || profile?.tier || "free";
    const tierColor = tier === "sports_premium" ? "#ef4444" : tier === "pro" ? "#a855f7" : "#22c55e";
    const TierIcon = tier === "sports_premium" ? Skull : tier === "pro" ? Crown : Leaf;

    return (
        <div className="min-h-screen pp-bg-0 text-white">
            <Nav />
            <section className="py-10 sm:py-14">
                <div className="max-w-3xl mx-auto px-4 sm:px-6">
                    <Button onClick={() => nav(-1)} variant="ghost" className="text-[#b7b7c6] hover:text-white mb-4" data-testid="profile-back-button">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>

                    {loading ? (
                        <div className="space-y-4">
                            <Skeleton className="h-8 w-40 pp-bg-1" />
                            <Skeleton className="h-64 rounded-xl pp-bg-1" />
                        </div>
                    ) : error ? (
                        <Card className="pp-bg-1 border-[#242436] text-white">
                            <CardContent className="p-8 text-center">
                                <p className="text-[#b7b7c6]">{error}</p>
                                <Button asChild className="mt-4 bg-[#6f3fec] hover:brightness-110 text-white">
                                    <Link to="/">Back to Picks</Link>
                                </Button>
                            </CardContent>
                        </Card>
                    ) : profile ? (
                        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                            <Card className="pp-bg-1 border-[#242436] text-white relative overflow-hidden" data-testid="profile-card" style={{ boxShadow: `0 0 0 1px ${profile.rank?.color || tierColor}30, 0 0 40px ${profile.rank?.color || tierColor}22` }}>
                                {profile.equipped_banner && (
                                    <div className="relative" data-testid="profile-banner">
                                        <BannerDisplay banner={profile.equipped_banner} height={180} className="rounded-none border-0" />
                                    </div>
                                )}
                                <div
                                    aria-hidden
                                    className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30"
                                    style={{ background: profile.rank?.color || tierColor }}
                                />
                                <CardContent className="p-8 relative">
                                    <div className="flex flex-col sm:flex-row items-start gap-6">
                                        <div
                                            className={`relative w-24 h-24 rounded-full flex items-center justify-center font-display text-4xl tracking-widest text-white flex-shrink-0 ${profile.equipped_banner ? "-mt-16 ring-4 ring-[#0a0a0f]" : ""}`}
                                            style={{
                                                background: `linear-gradient(135deg, ${profile.rank?.color || tierColor}, ${tierColor})`,
                                                boxShadow: `0 0 40px ${profile.rank?.color || tierColor}80`,
                                            }}
                                        >
                                            {(profile.display_name || "?").charAt(0).toUpperCase()}
                                            <span className="absolute -bottom-2 -right-2">
                                                <RankBadge rank={profile.rank} size="sm" showLabel={false} animate />
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h1 className="font-display text-3xl sm:text-4xl tracking-widest text-white" data-testid="profile-display-name">
                                                    {profile.display_name}
                                                </h1>
                                                <RoleBadge role={profile.role} size="lg" />
                                            </div>
                                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                                                <RankBadge rank={profile.rank} size="lg" animate />
                                                {profile.role === "admin" || profile.role === "support"
                                                    ? null
                                                    : <StreakBadge streak={profile.current_streak} best={profile.best_streak} size="lg" showBest />}
                                                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border" style={{ color: tierColor, borderColor: `${tierColor}66`, background: `${tierColor}1a` }}>
                                                    <TierIcon className="w-4 h-4" /> {TIER_LABELS[tier]}
                                                </span>
                                            </div>
                                            {profile.rank?.next_label && profile.rank.to_next > 0 && (
                                                <p className="mt-3 text-xs font-mono text-[#8b8ba0]">
                                                    {profile.rank.to_next} {profile.rank.to_next === 1 ? "day" : "days"} to <span style={{ color: profile.rank.color }}>{profile.rank.next_label}</span>
                                                </p>
                                            )}
                                            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                                <div className="pp-bg-0 rounded-md p-3 border border-[#242436]">
                                                    <p className="text-[10px] font-mono uppercase text-[#8b8ba0]">Streak</p>
                                                    <p className="mt-1 text-white font-display text-xl tracking-widest" data-testid="profile-streak">{profile.current_streak}d</p>
                                                </div>
                                                <div className="pp-bg-0 rounded-md p-3 border border-[#242436]">
                                                    <p className="text-[10px] font-mono uppercase text-[#8b8ba0] inline-flex items-center gap-1"><Trophy className="w-3 h-3" /> Best</p>
                                                    <p className="mt-1 text-white font-display text-xl tracking-widest" data-testid="profile-best-streak">{profile.best_streak}d</p>
                                                </div>
                                                <div className="pp-bg-0 rounded-md p-3 border border-[#242436]">
                                                    <p className="text-[10px] font-mono uppercase text-[#8b8ba0] inline-flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> Banners</p>
                                                    <p className="mt-1 text-white" data-testid="profile-banner-count">{profile.owned_banner_count || 0}</p>
                                                </div>
                                                <div className="pp-bg-0 rounded-md p-3 border border-[#242436]">
                                                    <p className="text-[10px] font-mono uppercase text-[#8b8ba0] inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Joined</p>
                                                    <p className="mt-1 text-white" data-testid="profile-joined">{formatDate(profile.joined_at)}</p>
                                                </div>
                                            </div>
                                            <div className="mt-5 flex gap-2 flex-wrap">
                                                {me && me.id !== profile.id && (me.role === "admin" || me.role === "support") && (
                                                    <Button onClick={async () => {
                                                        try {
                                                            const res = await api.post("/dm/conversations", { target_user_id: profile.id });
                                                            nav(`/messages/${res.data.id}`);
                                                        } catch (e) {}
                                                    }} className="bg-[#22d3ee] hover:brightness-110 text-[#062229] font-semibold" data-testid="profile-dm-button">
                                                        <MessageSquare className="w-4 h-4 mr-1" /> DM {profile.display_name}
                                                    </Button>
                                                )}
                                                {me?.id === profile.id && (
                                                    <>
                                                        <Button asChild variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white">
                                                            <Link to="/account">Edit Account</Link>
                                                        </Button>
                                                        <Button asChild className="bg-[#facc15] text-[#07130b] font-semibold">
                                                            <Link to="/shop" className="inline-flex items-center gap-1"><ShoppingBag className="w-4 h-4" /> Banner Shop</Link>
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Banner Vault - only visible on own profile */}
                            {isOwnProfile && (
                                <Card className="pp-bg-1 border-[#242436] text-white mt-6" data-testid="profile-banner-vault">
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-md" style={{ background: "#1f1a05", border: "1px solid #facc1555" }}>
                                                    <Archive className="w-4 h-4 text-[#facc15]" />
                                                </span>
                                                <div>
                                                    <p className="font-display text-xl tracking-widest">YOUR BANNER VAULT</p>
                                                    <p className="text-xs text-[#8b8ba0]">{vault.length === 0 ? "No banners yet \u2014 visit the shop to grab one." : `Click any banner to equip. Retired banners stay yours forever.`}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                {equippedId && (
                                                    <Button onClick={() => equipBanner({ id: equippedId, name: "Banner" })} disabled={busyBannerId !== null} variant="secondary" className="bg-[#14141c] border border-[#242436] hover:bg-[#1b1b26] text-white" data-testid="profile-unequip-button">
                                                        <X className="w-4 h-4 mr-1" /> Unequip
                                                    </Button>
                                                )}
                                                <Button asChild className="bg-[#facc15] text-[#07130b] font-semibold">
                                                    <Link to="/shop" className="inline-flex items-center gap-1"><ShoppingBag className="w-4 h-4" /> Browse Shop</Link>
                                                </Button>
                                            </div>
                                        </div>

                                        {vaultLoading ? (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-xl pp-bg-0" />)}</div>
                                        ) : vault.length === 0 ? (
                                            <div className="text-center py-8 text-[#8b8ba0] border border-dashed border-[#242436] rounded-xl" data-testid="profile-vault-empty">
                                                <ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">No banners owned yet.</p>
                                                <Button asChild className="mt-3 bg-[#a855f7] hover:brightness-110 text-white">
                                                    <Link to="/shop">Get your first banner</Link>
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="profile-vault-grid">
                                                {vault.map((b) => {
                                                    const rarityColor = RARITY_META[b.rarity]?.color || "#a855f7";
                                                    const isEquipped = equippedId === b.id;
                                                    const isBusy = busyBannerId === b.id;
                                                    return (
                                                        <button
                                                            key={b.id}
                                                            onClick={() => equipBanner(b)}
                                                            disabled={isBusy}
                                                            className="group text-left pp-bg-0 rounded-xl overflow-hidden border border-[#242436] hover:border-[#a855f7] transition-colors disabled:opacity-50"
                                                            style={isEquipped ? { boxShadow: `0 0 0 2px ${rarityColor}, 0 0 24px ${rarityColor}66`, borderColor: rarityColor } : { boxShadow: `0 0 0 1px ${rarityColor}33` }}
                                                            data-testid={`profile-vault-item-${b.slug}`}
                                                        >
                                                            <BannerDisplay banner={b} height={90} className="rounded-none border-0" />
                                                            <div className="p-3 flex items-center justify-between gap-2">
                                                                <div className="min-w-0">
                                                                    <p className="font-display text-sm tracking-widest text-white truncate">{b.name}</p>
                                                                    <p className="text-[10px] font-mono uppercase" style={{ color: rarityColor }}>
                                                                        {RARITY_META[b.rarity]?.label}
                                                                        {b.retired && <span className="ml-1 text-[#facc15]">· RETIRED</span>}
                                                                    </p>
                                                                </div>
                                                                <span className="flex-shrink-0">
                                                                    {isEquipped ? (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase" style={{ background: `${rarityColor}22`, color: rarityColor, border: `1px solid ${rarityColor}66` }}>
                                                                            <Check className="w-3 h-3" /> Equipped
                                                                        </span>
                                                                    ) : (
                                                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono uppercase text-[#b7b7c6] border border-[#242436] group-hover:border-[#a855f7] group-hover:text-white">
                                                                            <Sparkles className="w-3 h-3" /> Equip
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </motion.div>
                    ) : null}
                </div>
            </section>
            <Footer />
        </div>
    );
}
